
    import { getCurrentUser, onAuthChange, logOut } from '/src/firebase/auth.ts';
    import { getUserProfile, TIER_CONFIG } from '/src/api/UserProfileAPI.ts';
    import { httpsCallable } from 'firebase/functions';
    import { functions } from '/src/firebase/config.ts';

    // Smooth scroll for anchor links
    document.querySelectorAll('a[href^="#"]').forEach(anchor => {
      anchor.addEventListener('click', function (e) {
        e.preventDefault();
        const target = document.querySelector(this.getAttribute('href'));
        if (target) {
          target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });
    });

    // Update auth link based on login state
    const authLink = document.getElementById('authLink');
    const userDropdown = document.getElementById('user-dropdown');
    const userDropdownToggle = document.getElementById('user-dropdown-toggle');
    const userEmailSpan = document.getElementById('user-email');
    const logoutBtn = document.getElementById('logout-btn');

    // Track if toolbar animation has been triggered
    let toolbarAnimated = false;

    function triggerToolbarAnimations() {
      if (toolbarAnimated) return;
      toolbarAnimated = true;
      requestAnimationFrame(function() {
        requestAnimationFrame(function() {
          document.querySelectorAll('.header-toolbar-animate').forEach(function(el) {
            el.classList.add('toolbar-ready');
          });
        });
      });
    }

    // Contextual UI based on user tier
    function updateUIForUser(user, profile) {
      const heroTitle = document.querySelector('.hero-title');
      const heroSubtitle = document.querySelector('.hero-subtitle');
      const ctaButtons = document.querySelector('.cta-buttons');
      const finalCtaTitle = document.querySelector('.cta-title');
      const finalCtaSubtitle = document.querySelector('.cta-subtitle');
      const finalCtaButtons = document.querySelector('.cta-section .cta-buttons');

      if (!user) {
        // Reset to default for logged-out users
        heroTitle.innerHTML = 'Build Immersive VR Experiences<br>Without Code';
        heroSubtitle.textContent = 'The only 360° virtual tour builder designed for true VR. Generate and edit 360° environments with AI, stream professional videos with HLS, and create stunning galleries with interactive hotspots—viewable on any device including Meta Quest.';
        ctaButtons.innerHTML = `
          <a href="/dashboard/" class="ios-button ios-button-primary cta-primary" style="text-decoration: none;">Start Free</a>
          <a href="/viewer.html?id=vJpsWl5HToyZVT6J5R8k" class="ios-button ios-button-secondary cta-secondary" style="text-decoration: none;">View Demo</a>
        `;
        finalCtaTitle.textContent = 'Ready to Build Your First VR Experience?';
        finalCtaSubtitle.textContent = 'Join creators building the next generation of immersive content. No credit card required to start.';
        finalCtaButtons.innerHTML = `
          <a href="/dashboard/" class="ios-button ios-button-primary cta-primary" style="text-decoration: none;">Start Building Free</a>
          <a href="/discover/" class="ios-button ios-button-secondary cta-secondary" style="text-decoration: none;">Explore Gallery</a>
        `;
        updatePricingForUser(null);
        return;
      }

      const tier = profile?.tier || 'free';
      const tierConfig = TIER_CONFIG[tier];
      const displayName = profile?.displayName || user.email?.split('@')[0] || 'Creator';

      // Update hero based on tier
      if (tier === 'admin') {
        heroTitle.innerHTML = `Welcome back, ${displayName}`;
        heroSubtitle.textContent = 'You have full administrative access to XRGallery. Manage experiences, users, and platform settings from your dashboard.';
        ctaButtons.innerHTML = `
          <a href="/dashboard/" class="ios-button ios-button-primary cta-primary" style="text-decoration: none;">Go to Dashboard</a>
          <a href="/discover/" class="ios-button ios-button-secondary cta-secondary" style="text-decoration: none;">Browse Gallery</a>
        `;
      } else if (tier === 'enterprise') {
        heroTitle.innerHTML = `Welcome back, ${displayName}`;
        heroSubtitle.textContent = 'Create unlimited VR experiences with white-label branding, self-host export, and dedicated SLA support. Your enterprise tools are ready.';
        ctaButtons.innerHTML = `
          <a href="/dashboard/" class="ios-button ios-button-primary cta-primary" style="text-decoration: none;">Go to Dashboard</a>
          <a href="/discover/" class="ios-button ios-button-secondary cta-secondary" style="text-decoration: none;">Browse Gallery</a>
        `;
      } else if (tier === 'pro') {
        heroTitle.innerHTML = `Welcome back, ${displayName}`;
        heroSubtitle.textContent = 'You have access to 20 cloud-hosted experiences, white-label branding, and advanced analytics. Keep building amazing VR content!';
        ctaButtons.innerHTML = `
          <a href="/dashboard/" class="ios-button ios-button-primary cta-primary" style="text-decoration: none;">Go to Dashboard</a>
          <a href="/dashboard/analytics.html" class="ios-button ios-button-secondary cta-secondary" style="text-decoration: none;">View Analytics</a>
        `;
      } else if (tier === 'basic') {
        heroTitle.innerHTML = `Welcome back, ${displayName}`;
        heroSubtitle.textContent = 'You have 5GB storage and 100GB/mo bandwidth. Ready to unlock more features? Upgrade to Pro for 1TB bandwidth, white-label, and AI generation.';
        ctaButtons.innerHTML = `
          <a href="/dashboard/" class="ios-button ios-button-primary cta-primary" style="text-decoration: none;">Go to Dashboard</a>
          <a href="/dashboard/pricing.html" class="ios-button ios-button-secondary cta-secondary" style="text-decoration: none;">Upgrade to Pro</a>
        `;
      } else {
        // Free tier
        heroTitle.innerHTML = `Welcome back, ${displayName}`;
        heroSubtitle.textContent = 'Create unlimited self-hosted VR experiences for free. Ready for cloud hosting, analytics, and team features? Check out our paid plans.';
        ctaButtons.innerHTML = `
          <a href="/dashboard/" class="ios-button ios-button-primary cta-primary" style="text-decoration: none;">Go to Dashboard</a>
          <a href="/dashboard/pricing.html" class="ios-button ios-button-secondary cta-secondary" style="text-decoration: none;">View Plans</a>
        `;
      }

      // Update final CTA based on tier
      if (tier === 'admin' || tier === 'enterprise') {
        finalCtaTitle.textContent = 'Continue Creating Amazing Experiences';
        finalCtaSubtitle.textContent = 'Your unlimited access awaits. Jump into your dashboard and keep building.';
        finalCtaButtons.innerHTML = `
          <a href="/dashboard/" class="ios-button ios-button-primary cta-primary" style="text-decoration: none;">Open Dashboard</a>
          <a href="/discover/" class="ios-button ios-button-secondary cta-secondary" style="text-decoration: none;">Explore Gallery</a>
        `;
      } else if (tier === 'pro') {
        finalCtaTitle.textContent = 'Keep Building with Pro Features';
        finalCtaSubtitle.textContent = 'Leverage your white-label branding and advanced analytics to create client-ready experiences.';
        finalCtaButtons.innerHTML = `
          <a href="/dashboard/" class="ios-button ios-button-primary cta-primary" style="text-decoration: none;">Open Dashboard</a>
          <a href="/discover/" class="ios-button ios-button-secondary cta-secondary" style="text-decoration: none;">Explore Gallery</a>
        `;
      } else if (tier === 'basic') {
        finalCtaTitle.textContent = 'Unlock Your Full Potential';
        finalCtaSubtitle.textContent = 'Upgrade to Pro for white-label branding, advanced analytics, and 20 cloud-hosted experiences.';
        finalCtaButtons.innerHTML = `
          <a href="/dashboard/pricing.html" class="ios-button ios-button-primary cta-primary" style="text-decoration: none;">Upgrade to Pro</a>
          <a href="/dashboard/" class="ios-button ios-button-secondary cta-secondary" style="text-decoration: none;">Go to Dashboard</a>
        `;
      } else {
        // Free tier
        finalCtaTitle.textContent = 'Ready to Go Cloud?';
        finalCtaSubtitle.textContent = 'Self-hosting is great, but cloud hosting gives you analytics, direct uploads, and no server maintenance.';
        finalCtaButtons.innerHTML = `
          <a href="/dashboard/pricing.html" class="ios-button ios-button-primary cta-primary" style="text-decoration: none;">View Plans</a>
          <a href="/dashboard/" class="ios-button ios-button-secondary cta-secondary" style="text-decoration: none;">Go to Dashboard</a>
        `;
      }

      // Update pricing section to highlight current tier
      updatePricingForUser(tier);
    }

    // Update pricing cards based on user's current tier
    function updatePricingForUser(userTier) {
      const pricingCards = document.querySelectorAll('.pricing-card');

      pricingCards.forEach(card => {
        const tierName = card.querySelector('.pricing-name')?.textContent?.toLowerCase();
        const button = card.querySelector('.ios-button');

        // Remove any existing current-plan styling
        card.classList.remove('current-plan');
        card.style.removeProperty('border-color');

        if (!userTier || !button) return;

        if (tierName === userTier) {
          // This is the user's current plan
          card.classList.add('current-plan');
          card.style.borderColor = '#34C759';
          button.textContent = 'Current Plan';
          button.classList.remove('ios-button-primary');
          button.classList.add('ios-button-secondary');
          button.style.pointerEvents = 'none';
          button.style.opacity = '0.7';
        } else if (tierName === 'free' && userTier !== 'free') {
          // User has upgraded past free
          button.textContent = 'Included';
          button.style.pointerEvents = 'none';
          button.style.opacity = '0.5';
        } else if (
          (tierName === 'basic' && (userTier === 'pro' || userTier === 'enterprise' || userTier === 'admin')) ||
          (tierName === 'pro' && (userTier === 'enterprise' || userTier === 'admin'))
        ) {
          // User has a higher tier
          button.textContent = 'Included';
          button.style.pointerEvents = 'none';
          button.style.opacity = '0.5';
        } else {
          // This is an upgrade option
          button.textContent = 'Upgrade';
          button.style.pointerEvents = 'auto';
          button.style.opacity = '1';
          button.href = '/dashboard/pricing.html';
        }
      });
    }

    // Add tier badge next to user email in header
    function updateHeaderTierBadge(tier) {
      const existingBadge = document.getElementById('header-tier-badge');
      if (existingBadge) existingBadge.remove();

      if (!tier || tier === 'free') return;

      const userDropdownToggle = document.getElementById('user-dropdown-toggle');
      if (!userDropdownToggle) return;

      const badge = document.createElement('span');
      badge.id = 'header-tier-badge';
      badge.style.cssText = `
        display: inline-block;
        padding: 2px 8px;
        border-radius: 6px;
        font-size: 11px;
        font-weight: 600;
        text-transform: uppercase;
        margin-left: 8px;
      `;

      if (tier === 'admin') {
        badge.textContent = 'Admin';
        badge.style.background = 'linear-gradient(135deg, #8B5CF6, #A855F7)';
        badge.style.color = 'white';
      } else if (tier === 'enterprise') {
        badge.textContent = 'Enterprise';
        badge.style.background = 'linear-gradient(135deg, #3B82F6, #60A5FA)';
        badge.style.color = 'white';
      } else if (tier === 'pro') {
        badge.textContent = 'Pro';
        badge.style.background = 'linear-gradient(135deg, #F59E0B, #FBBF24)';
        badge.style.color = 'white';
      } else if (tier === 'basic') {
        badge.textContent = 'Basic';
        badge.style.background = 'linear-gradient(135deg, #10B981, #34D399)';
        badge.style.color = 'white';
      }

      userDropdownToggle.appendChild(badge);
    }

    onAuthChange(async (user) => {
      if (user) {
        authLink.style.display = 'none';
        userDropdown.style.display = 'block';
        if (userEmailSpan) userEmailSpan.textContent = user.email;

        // Fetch user profile and update UI based on tier
        try {
          const profile = await getUserProfile(user.uid);
          updateUIForUser(user, profile);
          updateHeaderTierBadge(profile?.tier);
        } catch (error) {
          console.error('Error fetching user profile:', error);
          updateUIForUser(user, null);
        }
      } else {
        authLink.style.display = 'inline-flex';
        userDropdown.style.display = 'none';
        updateUIForUser(null, null);
        updateHeaderTierBadge(null);
      }

      // Trigger toolbar animations after auth state is determined
      triggerToolbarAnimations();
    });

    // Dropdown toggle logic
    if (userDropdownToggle && userDropdown) {
      userDropdownToggle.addEventListener('click', (e) => {
        e.stopPropagation();
        userDropdown.classList.toggle('active');
      });

      document.addEventListener('click', (e) => {
        if (!userDropdown.contains(e.target)) {
          userDropdown.classList.remove('active');
        }
      });
    }

    // Logout logic
    if (logoutBtn) {
      logoutBtn.addEventListener('click', async () => {
        try {
          await logOut();
          window.location.reload();
        } catch (error) {
          console.error('Logout error:', error);
        }
      });
    }

    // Mobile menu toggle
    const hamburgerBtn = document.getElementById('hamburger-btn');
    const mobileMenu = document.getElementById('mobile-menu');
    const mobileMenuOverlay = document.getElementById('mobile-menu-overlay');
    const mobileUserEmail = document.getElementById('mobile-user-email');
    const mobileAuthLink = document.getElementById('mobile-auth-link');
    const mobileLogoutBtn = document.getElementById('mobile-logout-btn');

    function toggleMobileMenu() {
      mobileMenu.classList.toggle('active');
      mobileMenuOverlay.classList.toggle('active');
      document.body.style.overflow = mobileMenu.classList.contains('active') ? 'hidden' : '';
    }

    if (hamburgerBtn) hamburgerBtn.addEventListener('click', toggleMobileMenu);
    if (mobileMenuOverlay) mobileMenuOverlay.addEventListener('click', toggleMobileMenu);

    if (mobileLogoutBtn) {
      mobileLogoutBtn.addEventListener('click', async () => {
        toggleMobileMenu();
        try {
          await logOut();
          window.location.reload();
        } catch (error) {
          console.error('Logout error:', error);
        }
      });
    }

    // Keep mobile menu in sync with auth state
    onAuthChange((user) => {
      if (user) {
        if (mobileUserEmail) mobileUserEmail.textContent = user.email || 'Signed in';
        if (mobileAuthLink) mobileAuthLink.style.display = 'none';
        if (mobileLogoutBtn) mobileLogoutBtn.style.display = '';
      } else {
        if (mobileUserEmail) mobileUserEmail.textContent = 'Not signed in';
        if (mobileAuthLink) mobileAuthLink.style.display = '';
        if (mobileLogoutBtn) mobileLogoutBtn.style.display = 'none';
      }
    });

    // Scroll Reveal Animation Observer
    const scrollRevealElements = document.querySelectorAll('.scroll-reveal');

    const scrollObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const delay = entry.target.dataset.delay || 0;
          const delayMs = parseInt(delay) * 100; // 100ms per delay unit

          setTimeout(() => {
            entry.target.classList.add('visible');
          }, delayMs);

          // Unobserve after animation to save resources
          scrollObserver.unobserve(entry.target);
        }
      });
    }, {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    });

    scrollRevealElements.forEach((el) => scrollObserver.observe(el));

    // Magnetic button effect for mouse tracking
    const magneticButtons = document.querySelectorAll('.btn-magnetic');

    magneticButtons.forEach((btn) => {
      btn.addEventListener('mousemove', (e) => {
        const rect = btn.getBoundingClientRect();
        const x = e.clientX - rect.left - rect.width / 2;
        const y = e.clientY - rect.top - rect.height / 2;

        btn.style.setProperty('--mouse-x', `${x * 0.3}px`);
        btn.style.setProperty('--mouse-y', `${y * 0.3}px`);
      });

      btn.addEventListener('mouseleave', () => {
        btn.style.setProperty('--mouse-x', '0px');
        btn.style.setProperty('--mouse-y', '0px');
      });
    });

    // Card tilt effect on mouse move
    const tiltCards = document.querySelectorAll('.card-hover-tilt');

    tiltCards.forEach((card) => {
      card.addEventListener('mousemove', (e) => {
        const rect = card.getBoundingClientRect();
        const x = (e.clientX - rect.left) / rect.width;
        const y = (e.clientY - rect.top) / rect.height;

        const tiltX = (y - 0.5) * 10; // Max 5 degrees
        const tiltY = (x - 0.5) * -10;

        card.style.transform = `perspective(1000px) rotateX(${tiltX}deg) rotateY(${tiltY}deg) translateY(-4px)`;
      });

      card.addEventListener('mouseleave', () => {
        card.style.transform = 'perspective(1000px) rotateX(0deg) rotateY(0deg) translateY(0)';
      });
    });

    // Theme is now controlled by /theme.js

    // Newsletter signup handling
    const newsletterForm = document.getElementById('newsletter-form');
    const newsletterEmail = document.getElementById('newsletter-email');
    const newsletterSubmit = document.getElementById('newsletter-submit');
    const newsletterMessage = document.getElementById('newsletter-message');

    if (newsletterForm) {
      newsletterForm.addEventListener('submit', async function(e) {
        e.preventDefault();

        const email = newsletterEmail.value.trim();
        if (!email) return;

        // Disable form while submitting
        newsletterSubmit.disabled = true;
        newsletterSubmit.textContent = 'Subscribing...';
        newsletterMessage.textContent = '';
        newsletterMessage.className = 'newsletter-message';

        try {
          const subscribeNewsletter = httpsCallable(functions, 'subscribeNewsletter');
          const result = await subscribeNewsletter({
            email: email,
            source: 'landing_footer'
          });

          if (result.data.success) {
            newsletterMessage.textContent = 'Check your email to confirm your subscription!';
            newsletterMessage.className = 'newsletter-message success';
            newsletterEmail.value = '';
          } else {
            throw new Error(result.data.error || 'Subscription failed');
          }
        } catch (error) {
          console.error('Newsletter subscription error:', error);
          let errorMessage = 'Something went wrong. Please try again.';

          if (error.message?.includes('already subscribed')) {
            errorMessage = 'This email is already subscribed.';
          } else if (error.message?.includes('invalid email')) {
            errorMessage = 'Please enter a valid email address.';
          }

          newsletterMessage.textContent = errorMessage;
          newsletterMessage.className = 'newsletter-message error';
        } finally {
          newsletterSubmit.disabled = false;
          newsletterSubmit.textContent = 'Subscribe';
        }
      });
    }
  